import React, { useState } from 'react';
import {
  Information,
  withTranslation,
  Link,
  Trans,
  formatDate,
  formatNumber,
} from '@piwikpro/ui-components';
import { MarketingCratePaymentsState } from '@piwikpro/marketing-crate/redux';
import { connect, ConnectedProps } from 'react-redux';
import { withInject } from '@piwikpro/platform';
import { OrganizationState } from '@piwikpro/organization-crate';
import { TFunction } from 'react-i18next';
import RevokeCancelModal from '../RevokeCancelModal/RevokeCancelModal';

const mapStateToProps = (
  state: { organization: OrganizationState } & MarketingCratePaymentsState,
) => ({
  isLoading: state.payments.tiers.isLoading
    || state.payments.subscription.isLoading
    || state.organization.details.isLoading,
  failed: state.payments.tiers.failed,
  tiers: state.payments.tiers.details,
  subscription: state.payments.subscription,
  upcomingLicense: state.organization.details.license?.upcoming,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  t: TFunction
}

interface Props extends ReduxProps, InjectedProps { }

const ScheduledActionInformation: React.FunctionComponent<Props> = ({
  tiers, t, failed, isLoading, subscription, upcomingLicense,
}) => {
  const [revokeModalOpened, setRevokeModalOpened] = useState(false);

  if (failed || isLoading) return null;

  const variant = (() => {
    const scheduledChangeAction = subscription.details?.scheduled_change_action;

    if (scheduledChangeAction === 'cancel') { return 'cancel'; }
    if (scheduledChangeAction === 'update') { return 'downgrade'; }
    if (upcomingLicense) { return 'upcoming'; }
    return 'none';
  })();

  switch (variant) {
    case 'upcoming': {
      if (upcomingLicense === null) {
        throw new Error('upcomingLicense is null');
      }
      const formattedDate = formatDate(new Date(upcomingLicense.active_at), { format: 'LL' });

      return (
        <Information type="warning">
          {t('marketing.scheduled-action-information.upcoming-license.info-new', {
            type: upcomingLicense.plan.name,
            date: formattedDate,
          })}
        </Information>
      );
    }
    case 'cancel': {
      return (
        <>
          <Information type="warning">
            <Trans
              t={t}
              i18nKey="marketing.scheduled-action-information.cancel-30-days-grace.info"
              components={[
                <Link key="revoke-link" size="small" onClick={() => { setRevokeModalOpened(true); }}>
                  linkText
                </Link>,
              ]}
            />
          </Information>
          {revokeModalOpened && (<RevokeCancelModal onClose={() => { setRevokeModalOpened(false); }} />)}
        </>
      );
    }
    case 'downgrade': {
      const { details } = subscription;
      if (!details) {
        return null;
      }

      const catalogTier = tiers?.find(({ id }) => id === details.requested_price_id);
      const tier = catalogTier ?? (
        details.requested_price_id !== null
        && details.requested_traffic_limit !== null
        && details.requested_domains_limit !== null
        && details.requested_billing_cycle !== null
          ? {
            id: details.requested_price_id,
            traffic_limit: details.requested_traffic_limit,
            domains_limit: details.requested_domains_limit,
            billing_cycle: details.requested_billing_cycle,
          }
          : undefined
      );

      // Multi-product: requested_price_id may not be in the current product's /tiers list.
      // Prefer catalog match, then fall back to limits exposed on GET /tier.
      if (!tier) {
        return null;
      }

      const periodKey = tier.billing_cycle === 'year'
        ? 'marketing.scheduled-action-information.period.yearly'
        : 'marketing.scheduled-action-information.period.monthly';

      return (
        <Information type="warning">
          {t('marketing.scheduled-action-information.downgrade.info', {
            actionsPerMonth: formatNumber(Number(tier.traffic_limit)),
            domainsLimit: t('marketing.scheduled-action-information.domains_interval', { count: tier.domains_limit, postProcess: 'interval' }),
            billingPeriod: t(periodKey),
          })}
        </Information>
      );
    }
    default: return null;
  }
};

export default connector(withInject<InjectedProps>({})(
  withTranslation('administration')(ScheduledActionInformation as any),
));
